import {
  defaultRuntimePriority,
  IMPLEMENTED_RUNTIME_TYPES,
  SUBSCRIPTION_RUNTIME_TYPES,
  RESOURCE_CLASSES,
  RUNTIME_TYPES,
  type ResourceClass,
  type RuntimeProfile,
  type RuntimeType,
} from '@deskworks/shared-types';
import { LogIn } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Field } from '@/components/field';
import { invalidateRuntimeProfiles, providerName, RuntimeLoginDialog, SubscriptionStatus } from '@/components/runtime-login-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { prettyJson } from '@/lib/format';
import { parseJsonObject, useApiMutation, useWorkspace } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { RESOURCE_CLASS, RUNTIME_TYPE_FORM } from '@/lib/labels';
import { qk } from '@/lib/query-keys';

/** Where a runtime is usable: this workspace only, every workspace of the account, or the chosen ones. */
type Availability = 'workspace' | 'account' | 'selected';

function availabilityOf(profile: RuntimeProfile | undefined): Availability {
  if (!profile || profile.workspaceId) return 'workspace';
  return profile.sharedWorkspaceIds ? 'selected' : 'account';
}

export function RuntimeProfileDialog({
  workspaceId,
  profile,
  canShare = false,
  children,
}: {
  workspaceId: string;
  profile?: RuntimeProfile;
  /** Account admins choose whether the runtime is only this workspace's, or shared with other or all workspaces of the account. */
  canShare?: boolean;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [availability, setAvailability] = useState<Availability>('workspace');
  const [sharedWith, setSharedWith] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [runtimeType, setRuntimeType] = useState<RuntimeType>('anthropic_api');
  const [model, setModel] = useState('');
  const [resourceClass, setResourceClass] = useState<ResourceClass>('standard');
  const [config, setConfig] = useState('{}');
  const [credential, setCredential] = useState('');
  const [budget, setBudget] = useState('');
  // Empty: the default for the provider (subscriptions first).
  const [priority, setPriority] = useState('');
  const [loginProfile, setLoginProfile] = useState<RuntimeProfile | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(profile?.name ?? '');
    setRuntimeType(profile?.runtimeType ?? 'anthropic_api');
    setModel(profile?.model ?? '');
    setResourceClass(profile?.resourceClass ?? 'standard');
    // The budget has its own field; the JSON shows the remaining settings.
    const { monthlyBudgetUsd, ...rest } = profile?.config ?? {};
    setConfig(prettyJson(rest));
    setBudget(monthlyBudgetUsd != null ? String(monthlyBudgetUsd) : '');
    setCredential('');
    setPriority(profile ? String(profile.priority) : '');
    setAvailability(availabilityOf(profile));
    setSharedWith(profile?.sharedWorkspaceIds ?? [workspaceId]);
  }, [open, profile, workspaceId]);

  const accountId = useWorkspace(workspaceId).data?.accountId;
  const workspaces = useQuery({
    queryKey: qk.accountWorkspaces(accountId ?? ''),
    queryFn: () => api.accounts.workspaces(accountId!),
    enabled: open && canShare && !!accountId,
  });
  // Ids of deleted workspaces drop out on the next save.
  const chosen = sharedWith.filter((id) => !workspaces.data || workspaces.data.some((w) => w.id === id));
  const sharedError = canShare && availability === 'selected' && !chosen.length ? t('Choose at least one workspace') : null;
  const workspaceIds = availability === 'workspace' ? [workspaceId] : availability === 'account' ? null : chosen;
  const placementChanged =
    canShare &&
    (availability !== availabilityOf(profile) ||
      (availability === 'selected' && [...chosen].sort().join() !== [...(profile?.sharedWorkspaceIds ?? [])].sort().join()));

  const parsed = parseJsonObject(config);
  const budgetValue = budget.trim() === '' ? null : Number(budget.replace(',', '.'));
  const budgetError = budgetValue !== null && !(Number.isFinite(budgetValue) && budgetValue > 0) ? t('Enter an amount in USD, e.g. 50') : null;
  const configError = 'error' in parsed ? parsed.error : null;
  const priorityValue = priority.trim() === '' ? null : Number(priority);
  const priorityError = priorityValue !== null && !(Number.isInteger(priorityValue) && priorityValue >= 0 && priorityValue <= 10_000) ? t('Enter a whole number, e.g. 10') : null;
  const implemented = IMPLEMENTED_RUNTIME_TYPES.includes(runtimeType);
  const form = RUNTIME_TYPE_FORM[runtimeType];
  const isSubscription = SUBSCRIPTION_RUNTIME_TYPES.includes(runtimeType);

  const save = useApiMutation(
    () => {
      const body = {
        name: name.trim(),
        runtimeType,
        model: model.trim() || null,
        resourceClass,
        config: {
          ...('value' in parsed ? parsed.value : {}),
          ...(!isSubscription && budgetValue ? { monthlyBudgetUsd: budgetValue } : {}),
        },
        credential: form.needsCredential && credential ? credential : undefined,
        priority: priorityValue ?? (profile ? undefined : defaultRuntimePriority(runtimeType)),
      };
      const placement = placementChanged ? { workspaceIds } : {};
      if (!profile) return api.runtimeProfiles.create(workspaceId, { ...body, ...placement });
      return api.runtimeProfiles.update(profile.id, { ...body, ...placement });
    },
    {
      success: profile ? t('Profile saved') : t('Profile created'),
      invalidate: [qk.runtimeProfiles(workspaceId), qk.agents(workspaceId), qk.agentAll],
      onSuccess: (saved) => {
        // A shared runtime shows in other workspaces' lists.
        if (saved.workspaceId !== workspaceId || (profile && profile.workspaceId !== workspaceId)) invalidateRuntimeProfiles(queryClient);
        setOpen(false);
        // New subscription profile without a manual token: go straight to the login.
        if (!profile && SUBSCRIPTION_RUNTIME_TYPES.includes(saved.runtimeType) && !credential) setLoginProfile(saved);
        setCredential('');
      },
    },
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim() && !configError && !budgetError && !priorityError && !sharedError) save.mutate();
  };

  return (
    <>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <form onSubmit={onSubmit} className="grid gap-4" autoComplete="off">
          <DialogHeader>
            <DialogTitle>{profile ? t('Edit runtime') : t('New runtime')}</DialogTitle>
            <DialogDescription>
              {t('A runtime connects Deskworks to an AI provider (API key or subscription login) and sets a default model. Agents pick a runtime and can choose their own model. You can switch it at any time without creating the agent again.')}
            </DialogDescription>
          </DialogHeader>
          <Field label={t('Name')}>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('e.g. Claude standard')} required autoFocus />
          </Field>
          {canShare && (
            <>
              <Field
                label={t('Available in')}
                error={sharedError}
                hint={
                  availability === 'workspace'
                    ? t('Only agents in this workspace can use it.')
                    : availability === 'account'
                      ? t('Agents in every workspace of the account can use it, and they share its plan limits. Only account admins can change it.')
                      : t('Agents in these workspaces can use it, and they share its plan limits. Only account admins can change it.')
                }
              >
                <Select value={availability} onValueChange={(v) => setAvailability(v as Availability)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="workspace">{t('This workspace')}</SelectItem>
                    <SelectItem value="account">{t('All workspaces of the account')}</SelectItem>
                    <SelectItem value="selected">{t('Selected workspaces')}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {availability === 'selected' && (
                <div className="-mt-2 grid max-h-48 gap-1.5 overflow-y-auto rounded-md border p-2">
                  {workspaces.isLoading && <span className="text-xs text-muted-foreground">{t('Loading workspaces…')}</span>}
                  {workspaces.data?.map((w) => (
                    <label key={w.id} className="flex cursor-pointer items-center gap-2 text-sm">
                      <Checkbox
                        checked={sharedWith.includes(w.id)}
                        onChange={(e) => setSharedWith((ids) => (e.target.checked ? [...ids, w.id] : ids.filter((id) => id !== w.id)))}
                      />
                      <span className="truncate">{w.name}</span>
                      {w.id === workspaceId && <span className="text-xs text-muted-foreground">({t('this workspace')})</span>}
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('Provider')}>
              <Select value={runtimeType} onValueChange={(v) => setRuntimeType(v as RuntimeType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RUNTIME_TYPES.map((rt) => (
                    <SelectItem key={rt} value={rt} disabled={!IMPLEMENTED_RUNTIME_TYPES.includes(rt) && profile?.runtimeType !== rt}>
                      {RUNTIME_TYPE_FORM[rt].option}
                      {!IMPLEMENTED_RUNTIME_TYPES.includes(rt) && ` – ${t('coming soon')}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={t('Default model')} optional hint={form.modelHint ? `${form.modelHint} ${t('Agents can choose their own model.')}` : t('Agents can choose their own model.')}>
              <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder={form.modelPlaceholder} spellCheck={false} />
            </Field>
          </div>
          {!implemented && (
            <p className="-mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              {t('The worker doesn’t support this provider yet. You can already create the profile, but agents can’t work with it yet.')}
            </p>
          )}
          <Field
            label={t('Priority')}
            optional
            error={priorityError}
            hint={t('Agents on “Automatic” try runtimes from the lowest number up and take the next one when a runtime is used up or can’t be reached. If none is free, they try again an hour later.')}
          >
            <Input value={priority} onChange={(e) => setPriority(e.target.value)} inputMode="numeric" placeholder={String(defaultRuntimePriority(runtimeType))} />
          </Field>
          <Field label={t('Resources')} hint={RESOURCE_CLASS[resourceClass].description}>
            <Select value={resourceClass} onValueChange={(v) => setResourceClass(v as ResourceClass)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RESOURCE_CLASSES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {RESOURCE_CLASS[r].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {form.needsCredential && !isSubscription && (
            <Field
              label={t('Monthly budget (USD)')}
              optional
              error={budgetError}
              hint={t('Deskworks shows how much of it is left this month, from each run’s tokens at the provider’s list prices. It doesn’t stop agents.')}
            >
              <Input value={budget} onChange={(e) => setBudget(e.target.value)} inputMode="decimal" placeholder={t('e.g. 50')} />
            </Field>
          )}
          <Field label={t('More settings (JSON)')} optional error={configError} hint={t('For advanced users, e.g. maxTokens or temperature.')}>
            <Textarea value={config} onChange={(e) => setConfig(e.target.value)} rows={4} className="font-mono text-[13px]" spellCheck={false} />
          </Field>
          {form.needsCredential && isSubscription && (
            <div className="grid gap-3 rounded-lg border p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium">{t('Sign-in with {provider}', { provider: providerName(runtimeType) })}</span>
                {profile && profile.runtimeType === runtimeType && (
                  <span className="text-xs">
                    <SubscriptionStatus profile={profile} />
                  </span>
                )}
              </div>
              {profile && profile.runtimeType === runtimeType ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" variant={profile.hasCredential ? 'outline' : 'default'} size="sm" onClick={() => setLoginProfile(profile)}>
                    <LogIn /> {profile.hasCredential ? t('Reconnect') : t('Connect {provider}', { provider: providerName(runtimeType) })}
                  </Button>
                  <span className="text-xs text-muted-foreground">{t('You sign in directly with {provider}.', { provider: providerName(runtimeType) })}</span>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t('After creating the profile, you sign in directly with {provider} — Deskworks only stores the result, encrypted.', { provider: providerName(runtimeType) })}
                </p>
              )}
              {runtimeType === 'claude_subscription' && (
                <details className="group">
                  <summary className="cursor-pointer text-xs text-muted-foreground select-none hover:text-foreground">{t('Paste token manually')}</summary>
                  <div className="mt-2">
                    <Field
                    label={form.credentialLabel}
                    optional={!!profile?.hasCredential}
                    hint={
                      profile?.hasCredential
                        ? t('Leave empty to keep the saved credentials — or enter new ones to replace them.')
                        : `${form.credentialHint} ${t('Stored encrypted and never shown again.')}`
                    }
                  >
                    <Input
                      type="password"
                      value={credential}
                      onChange={(e) => setCredential(e.target.value)}
                      autoComplete="new-password"
                      spellCheck={false}
                      placeholder={profile?.hasCredential ? t('•••••••• (unchanged)') : ''}
                    />
                  </Field>
                  </div>
                </details>
              )}
            </div>
          )}
          {form.needsCredential && !isSubscription && (
            <div className="grid gap-3 rounded-lg border p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">{t('API key')}</span>
                {profile &&
                  profile.runtimeType === runtimeType &&
                  (profile.hasCredential ? <Badge tone="green">{t('API key saved')}</Badge> : <Badge tone="amber">{t('No API key')}</Badge>)}
              </div>
              <Field
                label={form.credentialLabel}
                optional={!!profile?.hasCredential}
                hint={
                  profile?.hasCredential
                    ? t('Leave empty to keep the saved credentials — or enter new ones to replace them.')
                    : `${form.credentialHint} ${t('Stored encrypted and never shown again.')}`
                }
              >
                <Input
                  type="password"
                  value={credential}
                  onChange={(e) => setCredential(e.target.value)}
                  autoComplete="new-password"
                  spellCheck={false}
                  placeholder={profile?.hasCredential ? t('•••••••• (unchanged)') : ''}
                />
              </Field>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={save.isPending} disabled={!name.trim() || !!configError || !!budgetError || !!priorityError || !!sharedError}>
              {profile ? t('Save') : isSubscription && !credential ? t('Create profile & connect') : t('Create profile')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    {loginProfile && (
      <RuntimeLoginDialog profile={loginProfile} open onOpenChange={(o) => !o && setLoginProfile(null)} />
    )}
    </>
  );
}
